import axios from "axios";
import type { AxiosInstance } from "axios";
import type { HYRequestInterceptors, HYRequestConfig } from "./type";
import router from "@/router";
import localCache from "@/utils/cache";

import { ElLoading } from "element-plus";
import { ILoadingInstance } from "element-plus/lib/components/loading";
// import { ILoadingInstance } from "element-plus/lib/el-loading/src/loading.type";
import { ElMessage } from "element-plus";

const DEAFULT_LOADING = true;

class HYRequest {
  instance: AxiosInstance;
  interceptors?: HYRequestInterceptors;
  showLoading: boolean;
  loading?: ILoadingInstance;

  constructor(config: HYRequestConfig) {
    // 创建axios实例
    this.instance = axios.create(config);

    // 保存基本信息
    this.showLoading = config.showLoading ?? DEAFULT_LOADING;
    this.interceptors = config.interceptors;

    // 使用拦截器
    // 1.从config中取出的拦截器是对应的实例的拦截器
    this.instance.interceptors.request.use(
      this.interceptors?.requestInterceptor,
      this.interceptors?.requestInterceptorCatch
    );
    this.instance.interceptors.response.use(
      this.interceptors?.responseInterceptor,
      this.interceptors?.responseInterceptorCatch
    );

    // 2.添加所有的实例都有的拦截器
    this.instance.interceptors.request.use(
      (config) => {
        // ① 请求前添加 Token
        const token = localStorage.getItem("token");
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (this.showLoading) {
          this.loading = ElLoading.service({
            lock: true,
            text: "正在请求数据....",
            background: "rgba(0, 0, 0, 0.5)",
          });
        }
        return config;
      },
      (err) => {
        return err;
      }
    );
    this.instance.interceptors.response.use(
      (res) => {
        // 移除 loading
        this.loading?.close();

        const data = res.data;

        // ① 如果返回错误码 444 —> 跳转到登录页
        if (data?.code === 444) {
          ElMessage.error("登录错误，请重新登录");
          localCache.deleteCache("token");
          router.push("/login");
          return Promise.resolve(null); // <- 不抛异常，阻断链
        }

        // ② 正常返回
        return data;
      },
      (err) => {
        this.loading?.close();

        // 如果后端直接返回 444 状态码（非 data.code）
        if (err.response?.status === 444) {
          ElMessage.error("登录错误，请重新登录");
          localCache.deleteCache("token");
          router.push("/login");
        }

        return Promise.reject(err);
      }
    );

    // this.instance.interceptors.response.use(
    //   (res) => {
    //     // console.log("最外层结果", res);

    //     // 将loading移除
    //     this.loading?.close();
    //     if (res.status && res.status === 200) {
    //       //将返回值解一层
    //       const data = res.data;
    //       return data;
    //     } else {
    //       ElMessage({
    //         showClose: true,
    //         message: "网络错误",
    //         type: "error",
    //       });
    //       return res;
    //     }
    //   },
    //   (err) => {
    //     console.log("404的错误~", err);
    //     // 将loading移除
    //     this.loading?.close();

    //     // 例子: 判断不同的HttpErrorCode显示不同的错误信息
    //     if (err.response.status === 404) {
    //       console.log("404的错误~");
    //     }
    //     return err;
    //   }
    // );
  }

  request<T = any>(config: HYRequestConfig<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      // 1.单个请求对请求config的处理
      if (config.interceptors?.requestInterceptor) {
        config = config.interceptors.requestInterceptor(config);
      }

      // 2.判断是否需要显示loading
      if (config.showLoading === false) {
        this.showLoading = config.showLoading;
      }
      // console.log("请求参数", config);

      this.instance
        .request<any, T>(config)
        .then((res) => {
          // 1.单个请求对数据的处理
          if (config.interceptors?.responseInterceptor) {
            res = config.interceptors.responseInterceptor(res);
          }
          // 2.将showLoading设置true, 这样不会影响下一个请求
          this.showLoading = DEAFULT_LOADING;

          // 3.将结果resolve返回出去
          // console.log("剥掉一层的结果", res);
          resolve(res);
        })
        .catch((err) => {
          // 将showLoading设置true, 这样不会影响下一个请求
          this.showLoading = DEAFULT_LOADING;
          reject(err);
          return err;
        });
    });
  }

  get<T = any>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: "GET" });
  }

  post<T = any>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: "POST" });
  }

  delete<T = any>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: "DELETE" });
  }

  patch<T = any>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: "PATCH" });
  }
}

export default HYRequest;
